import React, { useEffect, useState, useMemo } from 'react';
import { Plus, Play, Square, Trash2, ChevronDown, ChevronUp, PlayCircle, Crosshair, Bell, Trash, LayoutGrid, List, X } from 'lucide-react';
import { getTheme } from '../App';
import { getFavorites } from './Markets';
import { useWS, Strategy } from '../WSContext';
import ManualOrderModal from '../components/ManualOrderModal';
import AssetLogo, { useAssetLogos } from '../components/AssetLogo';
import { groupLabel } from '../lib/groupLabel';
import AlertModal, { AlertDraft } from '../components/AlertModal';

interface InstrumentSpec { base_min: number; base_tick: number; min_notional: number; base_imr: number; }

const ENTRY_CONDITIONS = [
  { value: 'none',       label: 'Koşul Yok' },
  { value: 'funding_gt', label: 'Funding Rate >' },
  { value: 'funding_lt', label: 'Funding Rate <' },
  { value: 'spread_gt',  label: 'Spread >' },
  { value: 'price_gt',   label: 'Fiyat >' },
  { value: 'price_lt',   label: 'Fiyat <' },
];
const PRICE_TYPES = [
  { value: 'best_bid',       label: 'Best Bid' },
  { value: 'best_ask',       label: 'Best Ask' },
  { value: 'best_direction', label: 'Yöne Göre' },
  { value: 'mid',            label: 'Mid Price' },
  { value: 'mark',           label: 'Mark Price' },
  { value: 'oracle',         label: 'Oracle Fiyatı' },
];

interface Props { dark?: boolean; }

function makeId() { return Math.random().toString(36).slice(2, 10); }
function fmtSym(s: string) { return s.replace('PERP_','').replace('_USDC','').replace('-PERP',''); }

function defaultStrategy(firstFav?: string): Strategy {
  return {
    id: makeId(), name: 'Yeni Strateji',
    symbol: firstFav || 'PERP_SOL_USDC', side: 'long',
    orderSize: 1, orderCount: 3, leverage: 10,
    entryCondition: 'funding_gt', entryThreshold: 0.001,
    priceType: 'best_direction', active: false,
    maxPositionSize: 0,
    tpConfig: { enabled: false, type: 'percent', value: 0.5 },
    strategyType: 'standard',
  };
}

function defaultTpOnly(firstFav?: string): Strategy {
  return {
    id: makeId(), name: 'TP Only',
    symbol: firstFav || 'PERP_SOL_USDC', side: 'long',
    orderSize: 0, orderCount: 3, leverage: 10,
    entryCondition: 'none', entryThreshold: 0,
    priceType: 'best_direction', active: false,
    maxPositionSize: 0,
    tpConfig: { enabled: true, type: 'percent', value: 0.5, mode: 'ladder', triggerPrice: 0, chunkSize: 0, chunkPriceType: 'best_direction' },
    strategyType: 'tp_only',
  };
}

// Stratejinin dolumlarını bir gruba bağlar. Tek seferlik emirlerdeki assign_group'tan
// farkı: burada SÜRESİZ bir niyet kuruluyor (set_group_pending), çünkü strateji
// günlerce çalışıp yüzlerce dolum üretebilir ve pencereli atama onları kaçırırdı.
// maxPositionSize'ı backend SEMBOLÜN TOPLAM pozisyonuyla karşılaştırır:
// abs(positionQty) >= maxPositionSize ise strateji tek emir atmadan bekler.
// Kullanıcının kafasındaki soru ise "buna ne kadar EKLEYEYİM" — 30554 DEEP
// üstüne 4000 yazınca strateji sessizce kilitlendi ve sebebi ancak data.json
// okunarak bulundu. Tavanı göreli yapmak çözüm değil: risk tavanı anlamını
// kaybederdik ve aktivasyonda alınan kalıcı bir snapshot her restart'ta
// katlanma riski taşırdı. Bunun yerine kilidi YARATMA ANINDA burada yakalıyoruz.
function MaxPosField({ value, symbol, side, positions, inp, muted, onChange }: any) {
  const pos  = (positions || []).find((p: any) => p.symbol === symbol);
  const cur  = Math.abs(Number(pos?.positionQty) || 0);
  const v    = Number(value) || 0;
  const dead = v > 0 && cur >= v * 0.999;   // backend'deki toleransın aynısı
  // Ters yön: backend abs() baktığı için long strateji short pozisyonu KÜÇÜLTÜR,
  // yani tavana hiç değmez. Toplama burada yanıltıcı olurdu, uyarmakla yetiniyoruz.
  const opposite = cur > 0 && pos?.side && side && pos.side !== side;
  return (
    <div>
      <label className={`text-xs block mb-1 ${muted}`}>
        Maks. Pozisyon{' '}
        <span className={muted} title="Sembolün TOPLAM pozisyonunu kapsar — stratejinin kendi birikimini değil. Mevcut pozisyonun bu değerin üstündeyse strateji hiç emir atmaz.">
          (0 = sınırsız, sembol toplamı)
        </span>
      </label>
      <input type="number" min="0" step="1" className={inp}
        value={value ?? 0} onChange={(e) => onChange(+e.target.value)} />
      {cur > 0 && (
        <div className="mt-1 text-[11px] leading-tight">
          <span className={opposite ? 'text-amber-400' : muted}
            title={opposite ? 'Pozisyon ters yönde. Backend |pozisyon| baktığı için bu strateji tavana değmeden çalışır.' : undefined}>
            şu an: {cur.toLocaleString('en-US')} ({pos.side})
          </span>
          {dead && !opposite && (
            <div className="mt-0.5 flex items-center gap-2 flex-wrap">
              <span className="text-red-400">bu ayarla hiç emir atmaz</span>
              <button type="button"
                className="px-1.5 py-0.5 rounded border border-blue-500/60 text-blue-400 hover:bg-blue-500/10"
                onClick={() => onChange(Math.round(cur + v))}>
                → {Math.round(cur + v).toLocaleString('en-US')} yap
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function GroupPicker({ value, groups, inp, muted, onPick, onNew }: any) {
  return (
    <div>
      <label className={`text-xs block mb-1 ${muted}`}
        title="Stratejinin dolumları bu gruba bağlanır. Strateji durunca bağ otomatik kaldırılır.">
        Gruba ata <span className={muted}>(opsiyonel)</span>
      </label>
      <select className={inp} value={value ?? 0}
        onChange={(e) => (e.target.value === 'new' ? onNew() : onPick(+e.target.value))}>
        <option value={0}>— gruba atama —</option>
        {groups.map((g: any) => (
          <option key={g.no} value={g.no}>
            {groupLabel(g, g.no)}{g.status === 'closed' ? ' (kapalı)' : ''} · {g.trades} işlem
          </option>
        ))}
        <option value="new">+ yeni grup aç</option>
      </select>
    </div>
  );
}

export default function Strategies({ dark = true }: Props) {
  const { strategies, setStrategies, connected, sendWs, subscribe, orderCounters, tickers: rawTickers, alerts, activeBotId, positions } = useWS() as any;
  // Perp grupları — stratejinin dolumlarını bağlayacağımız hedef.
  const [perpGroupList, setPerpGroupList] = React.useState<any[]>([]);
  const pendingGroupPick = React.useRef<((n: number) => void) | null>(null);
  React.useEffect(() => {
    sendWs?.({ type: 'get_perp_groups', payload: {} });
    const unsub = subscribe?.((m: any) => {
      if (m.type === 'perp_groups') setPerpGroupList(m.payload?.list || []);
      if (m.type === 'lp_group_created' && m.payload?.groupNo) {
        pendingGroupPick.current?.(Number(m.payload.groupNo));
        pendingGroupPick.current = null;
        sendWs?.({ type: 'get_perp_groups', payload: {} });
      }
    });
    return () => unsub?.();
  }, [sendWs, subscribe]);
  const [expanded, setExpanded]     = useState<Record<string, boolean>>({});
  const [favorites, setFavs]        = useState<string[]>(getFavorites);
  const [specs, setSpecs]           = useState<Record<string, InstrumentSpec>>({});
  const [showForm, setShowForm]     = useState(false);
  const [draft, setDraft]           = useState<Strategy>(() => defaultStrategy());
  const [editDrafts, setEditDrafts] = useState<Record<string, Strategy>>({});
  const [killActive, setKillActive]         = useState(false);
  const [showManualOrder, setShowManualOrder] = useState(false);
  const [showAlertModal, setShowAlertModal]   = useState(false);
  const t = getTheme(dark);

  // Aktif bota ait alertler
  const botAlerts: AlertDraft[] = (alerts && activeBotId) ? (alerts[activeBotId] || []) : [];

  const handleCreateAlert = (alert: AlertDraft) => {
    sendWs({ type: 'create_alert', payload: alert });
  };

  const handleDeleteAlert = (alertId: string) => {
    sendWs({ type: 'delete_alert', payload: { id: alertId } });
  };

  useEffect(() => {
    return subscribe((data: any) => {
      if (data.type === 'kill_switch_activated') {
        setKillActive(false);
        setStrategies((prev: Strategy[]) => prev.map(s => ({ ...s, active: false })));
      }
    });
  }, [subscribe, setStrategies]);

  useEffect(() => {
    const onFavChange = () => setFavs(getFavorites());
    window.addEventListener('favorites_changed', onFavChange);
    window.addEventListener('storage', onFavChange);
    return () => {
      window.removeEventListener('favorites_changed', onFavChange);
      window.removeEventListener('storage', onFavChange);
    };
  }, []);

  const hasAnyActive  = (strategies as Strategy[]).some(s => s.active);
  const hasAnyPassive = (strategies as Strategy[]).some(s => !s.active);

  const killSwitch = () => {
    setKillActive(true);
    sendWs({ type: 'kill_switch' });
  };

  const startAll = () => {
    setStrategies((prev: Strategy[]) => prev.map(s => s.active ? s : { ...s, active: true }));
  };

  // Aktif botun ticker sembollerini al — bunlar bot'a özgüdür
  const botSymbols: string[] = (rawTickers || []).map((tk: any) => tk.symbol).filter(Boolean);

  // Favori listesini aktif botun sembollerine göre süz;
  // eşleşen fav yoksa botun tüm sembollerini göster
  const symbolList: string[] = (() => {
    if (botSymbols.length === 0) return favorites.length > 0 ? favorites : ['PERP_SOL_USDC'];
    const botFavs = favorites.filter(f => botSymbols.includes(f));
    return botFavs.length > 0 ? botFavs : botSymbols;
  })();

  const fetchSpec = (symbol: string) => {
    if (specs[symbol]) return;
    fetch(`https://api.orderly.org/v1/public/info/${symbol}`)
      .then(r => r.json())
      .then(d => { if (d?.data) setSpecs(prev => ({ ...prev, [symbol]: d.data })); })
      .catch(() => {});
  };

  const openForm = () => {
    setDraft(defaultStrategy(symbolList[0]));
    setShowForm(true);
  };

  const confirmAdd = () => {
    if (!draft.name) return;
    setStrategies((prev: Strategy[]) => [...prev, { ...draft, id: makeId(), active: false }]);
    setShowForm(false);
    setDraft(defaultStrategy(symbolList[0]));
  };

  const cancelForm = () => {
    setShowForm(false);
    setDraft(defaultStrategy(symbolList[0]));
  };

  const update = (id: string, patch: Partial<Strategy>) => {
    setEditDrafts(prev => ({ ...prev, [id]: { ...prev[id], ...patch } }));
    if (patch.symbol) fetchSpec(patch.symbol);
  };

  const updateTp = (id: string, patch: Partial<Strategy['tpConfig']>) => {
    setEditDrafts(prev => ({
      ...prev,
      [id]: { ...prev[id], tpConfig: { ...prev[id]?.tpConfig, ...patch } },
    }));
  };

  const saveEdit = (id: string) => {
    const d = editDrafts[id];
    if (!d) return;
    setStrategies((prev: Strategy[]) => prev.map(s => s.id === id ? { ...d, active: s.active } : s));
    setExpanded(prev => ({ ...prev, [id]: false }));
  };

  const cancelEdit = (id: string) => {
    setEditDrafts(prev => { const n = { ...prev }; delete n[id]; return n; });
    setExpanded(prev => ({ ...prev, [id]: false }));
  };

  const updateDraft = (patch: Partial<Strategy>) => {
    setDraft(prev => ({ ...prev, ...patch }));
    if (patch.symbol) fetchSpec(patch.symbol);
  };

  const updateDraftTp = (patch: Partial<Strategy['tpConfig']>) => {
    setDraft(prev => ({ ...prev, tpConfig: { ...prev.tpConfig, ...patch } }));
  };

  const remove = (id: string) => {
    setStrategies((prev: Strategy[]) => prev.filter(s => s.id !== id));
  };

  const deleteInactive = () => {
    setStrategies((prev: Strategy[]) => prev.filter(s => s.active));
  };

  const hasAnyInactive = (strategies as Strategy[]).some(s => !s.active);

  const toggle = (id: string) => {
    setStrategies((prev: Strategy[]) => prev.map(s => s.id === id ? { ...s, active: !s.active } : s));
  };

  const inp  = `border rounded-lg px-2 py-1.5 text-xs focus:outline-none w-full ${t.inp}`;
  const card = `${t.card} border ${t.cardBorder} rounded-xl overflow-hidden`;

  // Logo haritası — Pozisyonlar'la aynı paylaşılan harita; yalnız bu sayfadaki
  // stratejilerin varlıkları isteniyor.
  useAssetLogos(sendWs, subscribe, (strategies as Strategy[]).map(x => x.symbol));

  // ── Görünüm: satır / ızgara ────────────────────────────────────────────────
  // binance1'de 27 strateji var; tam genişlikte 27 satır tek ekranda okunmuyor.
  // Seçim tarayıcıda saklanıyor, her açılışta hatırlanıyor.
  const [layout, setLayout] = useState<'row' | 'grid'>(() => {
    try { return (localStorage.getItem('bq_strategy_layout') as 'row' | 'grid') || 'row'; }
    catch { return 'row'; }
  });
  useEffect(() => {
    try { localStorage.setItem('bq_strategy_layout', layout); } catch {}
    // Düzen değişince açık düzenlemeyi kapat: satırda açık bir strateji varken
    // ızgaraya geçilince modal kendiliğinden açılıyordu (kullanıcı istemeden).
    setExpanded({});
  }, [layout]);

  // AKTİFLER ÜSTTE — her iki düzende de. 27 stratejinin 8'i aktifken aradığın
  // şey hemen hep aktif olan. sort kararlı olduğu için aynı gruptakilerin
  // kendi aralarındaki sırası bozulmuyor (liste her tikte zıplamasın).
  const sortedStrategies = useMemo(
    () => [...(strategies as Strategy[])].sort((a, b) => Number(!!b.active) - Number(!!a.active)),
    [strategies],
  );

  return (
    <div className="p-6 space-y-4">
      <div className="flex items-center justify-between">
        <h2 className={`text-xl font-bold ${t.text}`}>Stratejiler</h2>
        <div className="flex gap-2 items-center">
          {/* Satır / ızgara anahtarı — segment düğme, seçim tarayıcıda saklanıyor */}
          <div className={`flex rounded-lg border ${t.cardBorder} overflow-hidden mr-1`}>
            {([['row', List, 'Satır'], ['grid', LayoutGrid, 'Izgara']] as const).map(([mod, Icon, etiket]) => (
              <button key={mod} onClick={() => setLayout(mod)} title={etiket}
                className={`flex items-center gap-1.5 px-2.5 py-2 text-xs transition-colors ${
                  layout === mod ? 'bg-[#4A9FE8] text-[#061530] font-bold' : `${t.muted} hover:opacity-80`}`}>
                <Icon size={13} /> {etiket}
              </button>
            ))}
          </div>
          <button onClick={startAll} disabled={!hasAnyPassive}
            className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-medium transition-colors ${
              hasAnyPassive
                ? 'bg-emerald-700/30 border border-emerald-600 text-emerald-300 hover:bg-emerald-700 hover:text-white'
                : 'border border-gray-700 text-gray-600 cursor-not-allowed opacity-50'
            }`}>
            <PlayCircle size={13} /> Tümünü Başlat
          </button>
          <button onClick={killSwitch} disabled={killActive || !hasAnyActive}
            className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-bold border transition-colors ${
              hasAnyActive && !killActive
                ? 'border-red-500 bg-red-600/20 text-red-300 hover:bg-red-600 hover:text-white'
                : 'border-gray-700 text-gray-600 cursor-not-allowed opacity-50'
            }`}>
            <Square size={12} fill="currentColor" />
            {killActive ? 'Durduruluyor...' : 'Kill Switch'}
          </button>
          <button onClick={deleteInactive} disabled={!hasAnyInactive}
            className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-medium transition-colors ${
              hasAnyInactive
                ? 'border border-gray-500 text-gray-300 hover:bg-rose-900/40 hover:border-rose-500 hover:text-rose-300'
                : 'border border-gray-700 text-gray-600 cursor-not-allowed opacity-50'
            }`}>
            <Trash2 size={12} /> Kapalıları Yolla
          </button>
          <button onClick={() => sendWs({ type: 'cancel_all_orders' })}
            className={`px-4 py-2 rounded-lg text-xs font-medium ${t.btnSecondary}`}>
            Tüm Emirleri İptal
          </button>
          <button onClick={() => setShowManualOrder(true)}
            className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-medium border transition-colors border-[#4A9FE8]/60 text-[#4A9FE8] hover:bg-[#4A9FE8]/20`}>
            <Crosshair size={13} /> Manuel Emir
          </button>
          <button onClick={() => setShowAlertModal(true)}
            className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-medium border transition-colors border-yellow-500/60 text-yellow-400 hover:bg-yellow-500/20">
            <Bell size={13} /> Alert Oluştur
          </button>
          <button onClick={openForm}
            className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-bold bg-[#4A9FE8] hover:bg-[#e5a820] text-[#061530]">
            <Plus size={14} /> Strateji Ekle
          </button>
        </div>
      </div>

      {!connected && (
        <div className={`${t.card} border ${t.cardBorder} rounded-xl p-4 text-xs ${t.muted} text-center`}>
          Backend bağlantısı bekleniyor...
        </div>
      )}

      {/* ── Alert listesi ────────────────────────────────────────────────── */}
      {botAlerts.length > 0 && (
        <div className={`${t.card} border ${t.cardBorder} rounded-xl overflow-hidden`}>
          <div className={`flex items-center gap-2 px-4 py-3 border-b ${t.cardBorder}`}>
            <Bell size={13} className="text-yellow-400" />
            <span className={`text-xs font-semibold ${t.text}`}>Aktif Alertler</span>
            <span className="ml-auto text-xs text-gray-500">{botAlerts.filter(a => a.active).length} aktif / {botAlerts.length} toplam</span>
          </div>
          <div className="divide-y divide-white/5">
            {botAlerts.map(alert => {
              const condLabel: Record<string, string> = {
                price_gt:       'Fiyat >',
                price_lt:       'Fiyat <',
                funding_gt:     'Funding >',
                funding_lt:     'Funding <',
                bid_oracle_abs: 'Bid-Oracle Δ >',
                bid_oracle_pct: 'Bid-Oracle % >',
                ask_oracle_abs: 'Ask-Oracle Δ >',
                ask_oracle_pct: 'Ask-Oracle % >',
              };
              const isTriggered = !!alert.triggered_at && !alert.active;
              return (
                <div key={alert.id} className={`flex items-center gap-3 px-4 py-2.5 ${isTriggered ? 'opacity-50' : ''}`}>
                  <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${isTriggered ? 'bg-orange-400' : 'bg-emerald-400'}`} />
                  <span className={`text-xs font-mono font-semibold ${t.text} w-20 flex-shrink-0`}>
                    {alert.symbol.replace('-PERP', '')}
                  </span>
                  <span className={`text-xs ${t.muted} flex-1`}>
                    {condLabel[alert.condition] || alert.condition} {alert.threshold}
                    {alert.note ? <span className="ml-1.5 text-blue-400">[{alert.note}]</span> : null}
                  </span>
                  {isTriggered && (
                    <span className="text-[10px] text-orange-400 mr-2">Tetiklendi</span>
                  )}
                  <button onClick={() => handleDeleteAlert(alert.id)}
                    className="text-gray-600 hover:text-rose-400 transition-colors flex-shrink-0">
                    <Trash size={12} />
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {showManualOrder && (
        <ManualOrderModal dark={dark} onClose={() => setShowManualOrder(false)} />
      )}

      {showAlertModal && (
        <AlertModal
          dark={dark}
          symbols={botSymbols.length > 0 ? botSymbols : symbolList}
          onClose={() => setShowAlertModal(false)}
          onSubmit={handleCreateAlert}
        />
      )}

      {strategies.length === 0 && (
        <div className={`${t.card} border ${t.cardBorder} rounded-xl p-10 text-center ${t.muted} text-sm`}>
          Henüz strateji yok. "Strateji Ekle" ile başla.
        </div>
      )}

      {/* Izgarada 4 sütun. Çok sıkışık gelirse 3'e düşürülecek — bu NOT
          bilerek duruyor, denemeden karar verilmeyecek. */}
      <div className={layout === 'grid' ? 'grid grid-cols-4 gap-3' : 'space-y-4'}>
      {sortedStrategies.map(s => {
        const isOpen = expanded[s.id];
        const isPriceCondition = s.entryCondition === 'price_gt' || s.entryCondition === 'price_lt';
        const spec = specs[s.symbol];
        const maxLev  = spec ? Math.floor(1 / spec.base_imr) : null;
        const minSize = spec ? spec.base_min : null;
        const noCondition = s.entryCondition === 'none';

        const ed       = editDrafts[s.id] ?? s;   // edit draft — form bu değerlerle çalışır
        const filled   = orderCounters[s.id] ?? 0;
        const total    = s.orderCount;
        const isTpOnly = (s.strategyType ?? 'standard') === 'tp_only';

        // DÜZENLEME FORMU TEK YERDE TANIMLI. Satır düzeninde satırın altında,
        // ızgara düzeninde modal içinde gösteriliyor — iki ayrı form yazmak
        // bakımda kaçınılmaz olarak ayrışır (biri düzeltilir, öteki unutulur).
        // Sütun sayısı kaba göre: geniş satırda 4, dar modalda 2.
        const formCols = layout === 'grid' ? 'grid-cols-2' : 'grid-cols-4';
        const kapat = () => setExpanded(prev => ({ ...prev, [s.id]: false }));
        const formBody = (
          <>

                {/* TP Only info banner */}
                {isTpOnly && (
                  <div className={`flex items-center gap-2 mb-3 px-3 py-2 rounded-lg bg-emerald-900/30 border border-emerald-700/40 text-emerald-300 text-xs`}>
                    <Crosshair size={12} />
                    Mevcut pozisyona TP koyar — yeni emir açmaz. Pozisyon kapandığında strateji tamamlanır.
                  </div>
                )}

                <div className={`grid gap-3 mb-3 ${formCols}`}>
                  <div>
                    <label className={`text-xs block mb-1 ${t.muted}`}>Strateji Adı</label>
                    <input className={inp} value={ed.name} onChange={e => update(s.id, { name: e.target.value })} />
                  </div>
                  <div>
                    <label className={`text-xs block mb-1 ${t.muted}`}>Sembol</label>
                    <select className={inp} value={ed.symbol} onChange={e => update(s.id, { symbol: e.target.value })}>
                      {symbolList.map(sym => <option key={sym} value={sym}>{fmtSym(sym)}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className={`text-xs block mb-1 ${t.muted}`}>Yön</label>
                    <select className={inp} value={ed.side} onChange={e => update(s.id, { side: e.target.value as any })}>
                      <option value="long">Long</option>
                      <option value="short">Short</option>
                    </select>
                  </div>
                  {isTpOnly ? (
                    <GroupPicker value={(ed as any).groupNo} groups={perpGroupList}
                      inp={inp} muted={t.muted}
                      onPick={(n: number) => update(s.id, { groupNo: n } as any)}
                      onNew={() => {
                        pendingGroupPick.current = (n) => update(s.id, { groupNo: n } as any);
                        sendWs?.({ type: 'create_lp_group', payload: { kind: 'perp' } });
                      }} />
                  ) : (
                    <div>
                      <label className={`text-xs block mb-1 ${t.muted}`}>
                        Kaldıraç {maxLev && <span className="text-[#4A9FE8]">(maks {maxLev}x)</span>}
                      </label>
                      <input type="number" min="1" max={maxLev ?? 100} className={inp}
                        value={ed.leverage} onChange={e => update(s.id, { leverage: +e.target.value })} />
                    </div>
                  )}
                </div>

                {!isTpOnly && (
                  <>
                    <div className={`grid ${formCols} gap-3 mb-3`}>
                      <div>
                        <div className="flex items-center justify-between mb-1">
                          <label className={`text-xs ${t.muted}`}>
                            Emir Büyüklüğü {minSize != null && <span className="text-[#4A9FE8]">(min {minSize})</span>}
                          </label>
                          {minSize != null && maxLev != null && (
                            <button onClick={() => update(s.id, { orderSize: minSize, leverage: maxLev })}
                              className="text-[10px] px-2 py-0.5 rounded font-bold bg-[#4A9FE8]/20 text-[#4A9FE8] hover:bg-[#4A9FE8]/40">
                              Min/Maks
                            </button>
                          )}
                        </div>
                        <input type="number" step={spec?.base_tick ?? 0.001} min={minSize ?? 0} className={inp}
                          value={ed.orderSize} onChange={e => update(s.id, { orderSize: +e.target.value })} />
                      </div>
                      <div>
                        <label className={`text-xs block mb-1 ${t.muted}`}>Emir Sayısı</label>
                        <input type="number" min="1" className={inp} value={ed.orderCount} onChange={e => update(s.id, { orderCount: +e.target.value })} />
                      </div>
                      <div>
                        <label className={`text-xs block mb-1 ${t.muted}`}>Giriş Koşulu</label>
                        <select className={inp} value={ed.entryCondition} onChange={e => update(s.id, { entryCondition: e.target.value })}>
                          {ENTRY_CONDITIONS.map(ec => <option key={ec.value} value={ec.value}>{ec.label}</option>)}
                        </select>
                      </div>
                      <div>
                        <label className={`text-xs block mb-1 ${t.muted}`}>
                          {noCondition ? 'Eşik' : isPriceCondition ? 'Fiyat ($)' : 'Eşik (%)'}
                        </label>
                        <input type="number" step={isPriceCondition ? '0.01' : '0.0001'} min="0"
                          disabled={noCondition}
                          className={`${inp} ${noCondition ? 'opacity-40 cursor-not-allowed' : ''}`}
                          value={ed.entryThreshold}
                          onChange={e => update(s.id, { entryThreshold: +e.target.value })} />
                      </div>
                    </div>

                    {spec && (
                      <p className={`text-xs ${t.muted} mb-3 px-1`}>
                        Min. işlem tutarı: <span className={t.gold}>${spec.min_notional} USDC</span>
                      </p>
                    )}

                    <div className={`grid ${formCols} gap-3 mb-3`}>
                      <div>
                        <label className={`text-xs block mb-1 ${t.muted}`}>Fiyat Tipi</label>
                        <select className={inp} value={ed.priceType} onChange={e => update(s.id, { priceType: e.target.value })}>
                          {PRICE_TYPES.map(pt => <option key={pt.value} value={pt.value}>{pt.label}</option>)}
                        </select>
                      </div>
                      <MaxPosField value={ed.maxPositionSize} symbol={ed.symbol} side={ed.side}
                        positions={positions} inp={inp} muted={t.muted}
                        onChange={(n: number) => update(s.id, { maxPositionSize: n })} />
                      <GroupPicker value={(ed as any).groupNo} groups={perpGroupList}
                        inp={inp} muted={t.muted}
                        onPick={(n: number) => update(s.id, { groupNo: n } as any)}
                        onNew={() => {
                          pendingGroupPick.current = (n) => update(s.id, { groupNo: n } as any);
                          sendWs?.({ type: 'create_lp_group', payload: { kind: 'perp' } });
                        }} />
                    </div>
                  </>
                )}

                {/* TP */}
                <div className={`border-t ${t.cardBorder} pt-3`}>
                  <div className="flex items-center gap-3 mb-2">
                    <label className={`text-xs font-semibold ${t.muted}`}>Take Profit</label>
                    {!isTpOnly && (
                      <label className="flex items-center gap-1.5 cursor-pointer">
                        <input type="checkbox" checked={ed.tpConfig.enabled}
                          onChange={e => updateTp(s.id, { enabled: e.target.checked })}
                          className="accent-[#4A9FE8]" />
                        <span className={`text-xs ${t.muted}`}>Aktif</span>
                      </label>
                    )}
                  </div>
                  {(ed.tpConfig.enabled || isTpOnly) && (() => {
                    const tpMode = ed.tpConfig.mode ?? 'ladder';
                    return (
                      <>
                        {isTpOnly && (
                          <div className="mb-2">
                            <label className={`text-xs block mb-1 ${t.muted}`}>TP Modu</label>
                            <select className={inp} value={tpMode} onChange={e => updateTp(s.id, { mode: e.target.value as any })}>
                              <option value="ladder">Kademeli — N seviyede limit emir</option>
                              <option value="drip">Tetikli — Fiyat koşulunda parça parça kapat</option>
                            </select>
                          </div>
                        )}
                        {(!isTpOnly || tpMode === 'ladder') && (
                          <div className={`grid gap-3 ${isTpOnly ? 'grid-cols-3' : 'grid-cols-2'}`}>
                            {isTpOnly && (
                              <div>
                                <label className={`text-xs block mb-1 ${t.muted}`}>Kademe Sayısı</label>
                                <input type="number" min="1" max="20" className={inp}
                                  value={ed.orderCount} onChange={e => update(s.id, { orderCount: +e.target.value })} />
                              </div>
                            )}
                            <div>
                              <label className={`text-xs block mb-1 ${t.muted}`}>TP Tipi</label>
                              <select className={inp} value={ed.tpConfig.type} onChange={e => updateTp(s.id, { type: e.target.value as any })}>
                                <option value="percent">Yüzde (%)</option>
                                <option value="price">Sabit Fiyat ($)</option>
                              </select>
                            </div>
                            <div>
                              <label className={`text-xs block mb-1 ${t.muted}`}>
                                {isTpOnly && ed.tpConfig.type === 'percent' ? 'Kademe Adımı (%)' : ed.tpConfig.type === 'percent' ? 'Yüzde (%)' : 'Fiyat ($)'}
                              </label>
                              <input type="number" step="0.0001" min="0" className={inp}
                                value={ed.tpConfig.value} onChange={e => updateTp(s.id, { value: +e.target.value })} />
                            </div>
                          </div>
                        )}
                        {isTpOnly && tpMode === 'drip' && (
                          <div className="grid grid-cols-2 gap-3">
                            <div>
                              <label className={`text-xs block mb-1 ${t.muted}`}>
                                Tetik Fiyatı ($) <span className="text-emerald-500">{s.side === 'long' ? '≥' : '≤'}</span>
                              </label>
                              <input type="number" step="0.01" min="0" className={inp}
                                value={ed.tpConfig.triggerPrice ?? 0}
                                onChange={e => updateTp(s.id, { triggerPrice: +e.target.value })} />
                            </div>
                            <div>
                              <label className={`text-xs block mb-1 ${t.muted}`}>Parça Büyüklüğü</label>
                              <input type="number" step="0.01" min="0" className={inp}
                                value={ed.tpConfig.chunkSize ?? 0}
                                onChange={e => updateTp(s.id, { chunkSize: +e.target.value })} />
                            </div>
                            <div>
                              <label className={`text-xs block mb-1 ${t.muted}`}>Fiyat Tipi</label>
                              <select className={inp} value={ed.tpConfig.chunkPriceType ?? 'best_direction'}
                                onChange={e => updateTp(s.id, { chunkPriceType: e.target.value })}>
                                {PRICE_TYPES.map(pt => <option key={pt.value} value={pt.value}>{pt.label}</option>)}
                              </select>
                            </div>
                            <div>
                              <label className={`text-xs block mb-1 ${t.muted}`}>Maks Kapatılacak Miktar <span className={t.muted}>(0 = sınırsız)</span></label>
                              <input type="number" step="0.01" min="0" className={inp}
                                value={ed.tpConfig.maxTotalQty ?? 0}
                                onChange={e => updateTp(s.id, { maxTotalQty: +e.target.value })} />
                            </div>
                          </div>
                        )}
                      </>
                    );
                  })()}
                </div>

                {/* Kaydet / İptal */}
                <div className={`flex gap-3 pt-3 mt-1 border-t ${t.cardBorder}`}>
                  <button onClick={() => saveEdit(s.id)}
                    className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-bold bg-[#4A9FE8] hover:bg-[#e5a820] text-[#061530]">
                    Kaydet
                  </button>
                  <button onClick={() => cancelEdit(s.id)}
                    className={`px-4 py-2 rounded-lg text-xs font-medium ${t.btnSecondary}`}>
                    İptal
                  </button>
                </div>
          </>
        );

        return (
          <div key={s.id} className={card}>
            {layout === 'row' ? (
              <>
              {/* Header */}
              <div
                className={`px-4 py-3 border-b ${t.cardBorder} flex items-center justify-between cursor-pointer select-none`}
                onClick={() => {
                  const opening = !isOpen;
                  setExpanded(prev => ({ ...prev, [s.id]: opening }));
                  if (opening) {
                    setEditDrafts(prev => ({ ...prev, [s.id]: { ...s } }));
                    fetchSpec(s.symbol);
                  }
                }}
              >
                <div className="flex items-center gap-3">
                  <span className={`text-xs px-2 py-0.5 rounded font-bold ${
                    s.side === 'long' ? 'bg-emerald-900/50 text-emerald-400' : 'bg-rose-900/50 text-rose-400'
                  }`}>{s.side.toUpperCase()}</span>
                  <span className={`font-semibold text-sm ${t.text}`}>{s.name}</span>
                  <span className={`text-xs ${t.muted}`}>{fmtSym(s.symbol)} · {s.leverage}x</span>
                  {isTpOnly && <span className="text-xs px-2 py-0.5 rounded-full bg-emerald-900/50 text-emerald-400 font-bold flex items-center gap-1"><Crosshair size={9}/>TP Only</span>}
                  {s.active && <span className="text-xs px-2 py-0.5 rounded-full bg-[#4A9FE8]/20 text-[#4A9FE8] font-bold animate-pulse">AKTİF</span>}
                  {filled > 0 && (
                    <span className={`text-xs px-2 py-0.5 rounded-full font-mono ${filled >= total ? 'bg-emerald-900/50 text-emerald-400' : 'bg-orange-900/40 text-orange-300'}`}>
                      {filled}/{total} emir
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-2" onClick={e => e.stopPropagation()}>
                  <button onClick={() => toggle(s.id)}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs transition-colors ${
                      s.active ? 'bg-rose-700 hover:bg-rose-600 text-white' : 'bg-[#4A9FE8] hover:bg-[#e5a820] text-[#061530] font-bold'
                    }`}>
                    {s.active ? <><Square size={12}/> Durdur</> : <><Play size={12}/> Başlat</>}
                  </button>
                  <button onClick={() => remove(s.id)} className="p-1.5 text-rose-400 hover:text-rose-300">
                    <Trash2 size={14} />
                  </button>
                  <span className={`p-1.5 ${t.muted}`}>
                    {isOpen ? <ChevronUp size={14}/> : <ChevronDown size={14}/>}
                  </span>
                </div>
              </div>

              {/* Progress bar */}
              {filled > 0 && (
                <div className="px-4 py-1.5">
                  <div className={`w-full h-1 rounded-full ${dark ? 'bg-white/10' : 'bg-gray-200'}`}>
                    <div className="h-1 rounded-full bg-emerald-500 transition-all"
                      style={{ width: `${Math.min((filled / total) * 100, 100)}%` }} />
                  </div>
                </div>
              )}
              </>
            ) : (
              /* ── KOMPAKT KART ──────────────────────────────────────────────
                 Izgarada göz "HYPE" arar, strateji adını değil — ama kullanıcı
                 adı başlık istedi: ad üstte, parite hemen altında BÜYÜK. Giriş
                 koşulu en alt satırda. Tıklayınca düzenleme modal'ı açılıyor. */
              <div className="p-3 flex flex-col gap-2 cursor-pointer select-none h-full"
                onClick={() => {
                  setExpanded(prev => ({ ...prev, [s.id]: true }));
                  setEditDrafts(prev => ({ ...prev, [s.id]: { ...s } }));
                  fetchSpec(s.symbol);
                }}>
                <div className="flex items-start justify-between gap-2">
                  <span className={`text-xs font-semibold leading-tight truncate ${t.text}`} title={s.name}>
                    {s.name}
                  </span>
                  {s.active
                    ? <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-[#4A9FE8]/20 text-[#4A9FE8] font-bold animate-pulse shrink-0">AKTİF</span>
                    : <span className={`text-[9px] px-1.5 py-0.5 rounded-full shrink-0 ${t.muted} opacity-60`}>PASİF</span>}
                </div>

                <div className="flex items-center gap-1.5 flex-wrap">
                  <AssetLogo symbol={s.symbol} size={20} />
                  <span className={`text-[9px] px-1.5 py-0.5 rounded font-bold ${
                    s.side === 'long' ? 'bg-emerald-900/50 text-emerald-400' : 'bg-rose-900/50 text-rose-400'
                  }`}>{s.side.toUpperCase()}</span>
                  <span className={`text-sm font-bold ${t.gold}`}>{fmtSym(s.symbol)}</span>
                  <span className={`text-[10px] ${t.muted}`}>{s.leverage}x</span>
                  {isTpOnly && <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-emerald-900/50 text-emerald-400 font-bold">TP</span>}
                </div>

                <div className={`text-[10px] ${t.muted} truncate`}>
                  {s.orderCount} emir × {s.orderSize} · {s.priceType}
                </div>

                <div className="mt-auto">
                  <div className={`text-[10px] font-mono mb-1 ${filled >= total && filled > 0 ? t.positive : t.muted}`}>
                    {filled}/{total} emir
                  </div>
                  <div className={`w-full h-1 rounded-full ${dark ? 'bg-white/10' : 'bg-gray-200'}`}>
                    <div className="h-1 rounded-full bg-emerald-500 transition-all"
                      style={{ width: `${total > 0 ? Math.min((filled / total) * 100, 100) : 0}%` }} />
                  </div>
                </div>

                {/* Giriş koşulu — en alt satır */}
                <div className={`text-[10px] font-mono truncate ${t.muted}`}
                  title={`${s.entryCondition} ${s.entryThreshold ?? ''}`}>
                  {s.entryCondition === 'none'
                    ? 'koşulsuz giriş'
                    : `${s.entryCondition} ${s.entryThreshold ?? ''}`}
                </div>

                <div className="flex items-center gap-1.5" onClick={e => e.stopPropagation()}>
                  <button onClick={() => toggle(s.id)}
                    className={`flex-1 flex items-center justify-center gap-1 px-2 py-1.5 rounded-lg text-[10px] transition-colors ${
                      s.active ? 'bg-rose-700 hover:bg-rose-600 text-white' : 'bg-[#4A9FE8] hover:bg-[#e5a820] text-[#061530] font-bold'
                    }`}>
                    {s.active ? <><Square size={10}/> Durdur</> : <><Play size={10}/> Başlat</>}
                  </button>
                  <button onClick={() => remove(s.id)} className="p-1.5 text-rose-400 hover:text-rose-300">
                    <Trash2 size={12} />
                  </button>
                </div>
              </div>
            )}

            {/* Form kabı — içerik yukarıdaki `formBody` */}
            {isOpen && layout === 'row' && (
              <div className={`p-4 ${dark ? 'bg-[#030d1f]/60' : 'bg-blue-50/60'}`}>
                {formBody}
              </div>
            )}

            {/* Izgarada: kompakt modal. Dashboard'daki strateji oluşturma
                penceresinden dar tutuldu (600px), sütunlar 2'ye indi. */}
            {isOpen && layout === 'grid' && (
              <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/70"
                onClick={kapat}>
                <div className={`${card} w-full max-w-[600px] max-h-[86vh] overflow-y-auto`}
                  onClick={e => e.stopPropagation()}>
                  <div className={`px-4 py-2.5 border-b ${t.cardBorder} flex items-center justify-between sticky top-0 z-10 ${dark ? 'bg-[#061530]' : 'bg-white'}`}>
                    <div className="flex items-center gap-2 min-w-0">
                      <span className={`text-xs px-2 py-0.5 rounded font-bold shrink-0 ${
                        s.side === 'long' ? 'bg-emerald-900/50 text-emerald-400' : 'bg-rose-900/50 text-rose-400'
                      }`}>{s.side.toUpperCase()}</span>
                      <span className={`text-sm font-semibold truncate ${t.text}`}>{s.name}</span>
                      <span className={`text-xs shrink-0 ${t.muted}`}>{fmtSym(s.symbol)}</span>
                    </div>
                    <button onClick={kapat} className={`p-1 shrink-0 ${t.muted} hover:opacity-70`}>
                      <X size={16} />
                    </button>
                  </div>
                  <div className="p-3">{formBody}</div>
                </div>
              </div>
            )}
          </div>
        );
      })}
      </div>

      {/* Yeni Strateji Formu */}
      {showForm && (() => {
        const s = draft;
        const draftIsTpOnly = (s.strategyType ?? 'standard') === 'tp_only';
        const isPriceCondition = s.entryCondition === 'price_gt' || s.entryCondition === 'price_lt';
        const spec    = specs[s.symbol];
        const maxLev  = spec ? Math.floor(1 / spec.base_imr) : null;
        const minSize = spec ? spec.base_min : null;
        const noCondition = s.entryCondition === 'none';
        return (
          <div className={`${t.card} border ${t.cardBorder} rounded-xl overflow-hidden`}>
            <div className={`px-4 py-3 border-b ${t.cardBorder} flex items-center gap-2`}>
              {draftIsTpOnly
                ? <><Crosshair size={14} className="text-emerald-400" /><span className={`text-sm font-semibold text-emerald-400`}>Yeni TP Only Strateji</span></>
                : <span className={`text-sm font-semibold ${t.text}`}>Yeni Strateji</span>
              }
            </div>
            <div className={`p-4 ${dark ? 'bg-[#030d1f]/60' : 'bg-blue-50/60'}`}>

              {/* TP Only info banner */}
              {draftIsTpOnly && (
                <div className="flex items-center gap-2 mb-3 px-3 py-2 rounded-lg bg-emerald-900/30 border border-emerald-700/40 text-emerald-300 text-xs">
                  <Crosshair size={12} />
                  Mevcut pozisyona TP koyar — yeni emir açmaz. Pozisyon kapandığında strateji tamamlanır.
                </div>
              )}

              <div className="grid gap-3 mb-3 grid-cols-4">
                <div>
                  <label className={`text-xs block mb-1 ${t.muted}`}>Strateji Adı</label>
                  <input className={inp} value={s.name} onChange={e => updateDraft({ name: e.target.value })} />
                </div>
                <div>
                  <label className={`text-xs block mb-1 ${t.muted}`}>Sembol</label>
                  <select className={inp} value={s.symbol} onChange={e => updateDraft({ symbol: e.target.value })}>
                    {symbolList.map(sym => <option key={sym} value={sym}>{fmtSym(sym)}</option>)}
                  </select>
                </div>
                <div>
                  <label className={`text-xs block mb-1 ${t.muted}`}>Yön</label>
                  <select className={inp} value={s.side} onChange={e => updateDraft({ side: e.target.value as any })}>
                    <option value="long">Long</option>
                    <option value="short">Short</option>
                  </select>
                </div>
                {/* TP Only'de kaldıraç yok (pozisyon zaten açık) — o hücreyi grup
                    kutusu alıyor; aşağıdaki emir bloğu TP Only'de hiç çizilmediği
                    için grup kutusu oraya konsaydı TP Only'de kaybolurdu. */}
                {draftIsTpOnly ? (
                  <GroupPicker value={(s as any).groupNo} groups={perpGroupList}
                    inp={inp} muted={t.muted}
                    onPick={(n: number) => updateDraft({ groupNo: n } as any)}
                    onNew={() => {
                      pendingGroupPick.current = (n) => updateDraft({ groupNo: n } as any);
                      sendWs?.({ type: 'create_lp_group', payload: { kind: 'perp' } });
                    }} />
                ) : (
                  <div>
                    <label className={`text-xs block mb-1 ${t.muted}`}>
                      Kaldıraç {maxLev && <span className="text-[#4A9FE8]">(maks {maxLev}x)</span>}
                    </label>
                    <input type="number" min="1" max={maxLev ?? 100} className={inp}
                      value={s.leverage} onChange={e => updateDraft({ leverage: +e.target.value })} />
                  </div>
                )}
              </div>

              {!draftIsTpOnly && (
                <>
                  <div className="grid grid-cols-4 gap-3 mb-3">
                    <div>
                      <div className="flex items-center justify-between mb-1">
                        <label className={`text-xs ${t.muted}`}>
                          Emir Büyüklüğü {minSize != null && <span className="text-[#4A9FE8]">(min {minSize})</span>}
                        </label>
                        {minSize != null && maxLev != null && (
                          <button onClick={() => updateDraft({ orderSize: minSize, leverage: maxLev })}
                            className="text-[10px] px-2 py-0.5 rounded font-bold bg-[#4A9FE8]/20 text-[#4A9FE8] hover:bg-[#4A9FE8]/40">
                            Min/Maks
                          </button>
                        )}
                      </div>
                      <input type="number" step={spec?.base_tick ?? 0.001} min={minSize ?? 0} className={inp}
                        value={s.orderSize} onChange={e => updateDraft({ orderSize: +e.target.value })} />
                    </div>
                    <div>
                      <label className={`text-xs block mb-1 ${t.muted}`}>Emir Sayısı</label>
                      <input type="number" min="1" className={inp} value={s.orderCount} onChange={e => updateDraft({ orderCount: +e.target.value })} />
                    </div>
                    <div>
                      <label className={`text-xs block mb-1 ${t.muted}`}>Giriş Koşulu</label>
                      <select className={inp} value={s.entryCondition} onChange={e => updateDraft({ entryCondition: e.target.value })}>
                        {ENTRY_CONDITIONS.map(ec => <option key={ec.value} value={ec.value}>{ec.label}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className={`text-xs block mb-1 ${t.muted}`}>
                        {noCondition ? 'Eşik' : isPriceCondition ? 'Fiyat ($)' : 'Eşik (%)'}
                      </label>
                      <input type="number" step={isPriceCondition ? '0.01' : '0.0001'} min="0"
                        disabled={noCondition}
                        className={`${inp} ${noCondition ? 'opacity-40 cursor-not-allowed' : ''}`}
                        value={s.entryThreshold}
                        onChange={e => updateDraft({ entryThreshold: +e.target.value })} />
                    </div>
                  </div>

                  {spec && (
                    <p className={`text-xs ${t.muted} mb-3 px-1`}>
                      Min. işlem tutarı: <span className={t.gold}>${spec.min_notional} USDC</span>
                    </p>
                  )}

                  <div className="grid grid-cols-4 gap-3 mb-3">
                    <div>
                      <label className={`text-xs block mb-1 ${t.muted}`}>Fiyat Tipi</label>
                      <select className={inp} value={s.priceType} onChange={e => updateDraft({ priceType: e.target.value })}>
                        {PRICE_TYPES.map(pt => <option key={pt.value} value={pt.value}>{pt.label}</option>)}
                      </select>
                    </div>
                    <MaxPosField value={s.maxPositionSize} symbol={s.symbol} side={s.side}
                      positions={positions} inp={inp} muted={t.muted}
                      onChange={(n: number) => updateDraft({ maxPositionSize: n })} />
                    <GroupPicker value={(s as any).groupNo} groups={perpGroupList}
                      inp={inp} muted={t.muted}
                      onPick={(n: number) => updateDraft({ groupNo: n } as any)}
                      onNew={() => {
                        pendingGroupPick.current = (n) => updateDraft({ groupNo: n } as any);
                        sendWs?.({ type: 'create_lp_group', payload: { kind: 'perp' } });
                      }} />
                  </div>
                </>
              )}

              <div className={`border-t ${t.cardBorder} pt-3 mb-4`}>
                <div className="flex items-center gap-3 mb-2">
                  <label className={`text-xs font-semibold ${t.muted}`}>Take Profit</label>
                  {!draftIsTpOnly && (
                    <label className="flex items-center gap-1.5 cursor-pointer">
                      <input type="checkbox" checked={s.tpConfig.enabled}
                        onChange={e => updateDraftTp({ enabled: e.target.checked })}
                        className="accent-[#4A9FE8]" />
                      <span className={`text-xs ${t.muted}`}>Aktif</span>
                    </label>
                  )}
                </div>
                {(s.tpConfig.enabled || draftIsTpOnly) && (() => {
                  const tpMode = s.tpConfig.mode ?? 'ladder';
                  return (
                    <>
                      {draftIsTpOnly && (
                        <div className="mb-2">
                          <label className={`text-xs block mb-1 ${t.muted}`}>TP Modu</label>
                          <select className={inp} value={tpMode} onChange={e => updateDraftTp({ mode: e.target.value as any })}>
                            <option value="ladder">Kademeli — N seviyede limit emir</option>
                            <option value="drip">Tetikli — Fiyat koşulunda parça parça kapat</option>
                          </select>
                        </div>
                      )}
                      {(!draftIsTpOnly || tpMode === 'ladder') && (
                        <div className={`grid gap-3 ${draftIsTpOnly ? 'grid-cols-3' : 'grid-cols-2'}`}>
                          {draftIsTpOnly && (
                            <div>
                              <label className={`text-xs block mb-1 ${t.muted}`}>Kademe Sayısı</label>
                              <input type="number" min="1" max="20" className={inp}
                                value={s.orderCount} onChange={e => updateDraft({ orderCount: +e.target.value })} />
                            </div>
                          )}
                          <div>
                            <label className={`text-xs block mb-1 ${t.muted}`}>TP Tipi</label>
                            <select className={inp} value={s.tpConfig.type} onChange={e => updateDraftTp({ type: e.target.value as any })}>
                              <option value="percent">Yüzde (%)</option>
                              <option value="price">Sabit Fiyat ($)</option>
                            </select>
                          </div>
                          <div>
                            <label className={`text-xs block mb-1 ${t.muted}`}>
                              {draftIsTpOnly && s.tpConfig.type === 'percent' ? 'Kademe Adımı (%)' : s.tpConfig.type === 'percent' ? 'Yüzde (%)' : 'Fiyat ($)'}
                            </label>
                            <input type="number" step="0.0001" min="0" className={inp}
                              value={s.tpConfig.value} onChange={e => updateDraftTp({ value: +e.target.value })} />
                          </div>
                        </div>
                      )}
                      {draftIsTpOnly && tpMode === 'drip' && (
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <label className={`text-xs block mb-1 ${t.muted}`}>
                              Tetik Fiyatı ($) <span className="text-emerald-500">{s.side === 'long' ? '≥' : '≤'}</span>
                            </label>
                            <input type="number" step="0.01" min="0" className={inp}
                              value={s.tpConfig.triggerPrice ?? 0}
                              onChange={e => updateDraftTp({ triggerPrice: +e.target.value })} />
                          </div>
                          <div>
                            <label className={`text-xs block mb-1 ${t.muted}`}>Parça Büyüklüğü</label>
                            <input type="number" step="0.01" min="0" className={inp}
                              value={s.tpConfig.chunkSize ?? 0}
                              onChange={e => updateDraftTp({ chunkSize: +e.target.value })} />
                          </div>
                          <div>
                            <label className={`text-xs block mb-1 ${t.muted}`}>Fiyat Tipi</label>
                            <select className={inp} value={s.tpConfig.chunkPriceType ?? 'best_direction'}
                              onChange={e => updateDraftTp({ chunkPriceType: e.target.value })}>
                              {PRICE_TYPES.map(pt => <option key={pt.value} value={pt.value}>{pt.label}</option>)}
                            </select>
                          </div>
                          <div>
                            <label className={`text-xs block mb-1 ${t.muted}`}>Maks Kapatılacak Miktar <span className={t.muted}>(0 = sınırsız)</span></label>
                            <input type="number" step="0.01" min="0" className={inp}
                              value={s.tpConfig.maxTotalQty ?? 0}
                              onChange={e => updateDraftTp({ maxTotalQty: +e.target.value })} />
                          </div>
                        </div>
                      )}
                    </>
                  );
                })()}
              </div>

              <div className="flex gap-3">
                <button onClick={confirmAdd}
                  className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-bold bg-[#4A9FE8] hover:bg-[#e5a820] text-[#061530]">
                  <Plus size={13} /> {draftIsTpOnly ? 'TP Only Ekle' : 'Strateji Ekle'}
                </button>
                <button onClick={cancelForm}
                  className={`px-4 py-2 rounded-lg text-xs font-medium ${t.btnSecondary}`}>
                  İptal
                </button>
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
}
